import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { AudioService } from '@core/audio/audio.service';
import { CursorService } from '@core/cursor/cursor.service';
import { LpCanvasService } from '@core/lp/lp-canvas.service';
import { CanvasRendererComponent } from '@features/canvas/canvas-renderer/canvas-renderer.component';
import { ICanvasElement, IDragEvent } from '@features/canvas/canvas.types';
import { HudButtonComponent } from '@features/hud/hud-button/hud-button.component';
import { LpMissionService } from '@features/lp/lp-mission/lp-mission.service';
import { StylesService } from '@features/styles/styles.service';
import { useElementSize } from '@shared/angular.utils';
import { add, isInside, multiply, subtract } from '@shared/vector.utils';
import { workaroundCanvasRatio } from '@shared/workarounds.utils';
import { LpDragAndDropActivity } from '@typings/lp/generated';
import { Id } from '@typings/id';
import { Vector2CanvasNormalized } from '@typings/vectors';

@Component({
  selector: 'lp-drag-and-drop-activity',
  imports: [CanvasRendererComponent, HudButtonComponent],
  templateUrl: './lp-drag-and-drop-activity.component.html',
  styleUrl: './lp-drag-and-drop-activity.component.css',
})
export class LpDragAndDropActivityComponent {
  readonly _stylesService = inject(StylesService);
  activity = input.required<LpDragAndDropActivity>();
  elements = signal<ICanvasElement[]>([]);
  size = computed(() => workaroundCanvasRatio(this.activity().canvasRatio));
  sizeInPixels = useElementSize();
  lastZIndex = signal(0);
  overrides = signal<Record<Id, Partial<{ position: Vector2CanvasNormalized; zIndex: number }>>>({});
  targets = signal<Record<Id, Id | undefined>>({});
  automaticContinue = computed(() => this.activity().instantlyContinue);
  canContinue = computed(() => Object.values(this.targets()).every((target) => target !== undefined));
  readonly #lpCanvasService = inject(LpCanvasService);
  readonly #lpMissionService = inject(LpMissionService);
  readonly #audioService = inject(AudioService);
  readonly #cursorService = inject(CursorService);

  constructor() {
    effect(async () => {
      // TODO: currently when the game state changes, the elements list is reset (this means drag positions etc are lost)
      this.#lpCanvasService.subscribeToShowIfChanges();

      this.elements.set([
        await this.#lpCanvasService.convertBackground(this.activity().background, this.size()),
        ...(await this.#lpCanvasService.convertDraggables(this.activity().draggables, this.sizeInPixels())),
        ...(await this.#lpCanvasService.convertVisualElements(this.activity().visualElements, this.sizeInPixels())),
        ...(await this.#lpCanvasService.convertDropTargets(this.activity().targets, this.sizeInPixels())),
      ]);

      this.targets.set(this.activity().targets.reduce((acc, target) => ({ [target.uid]: undefined }), {}));
    });
    effect(async () => {
      if (this.automaticContinue() && this.canContinue()) await this.continue();
    });
  }

  async grab(event: IDragEvent) {
    this.#cursorService.setGlobalCursor('grabbing');
    this.#audioService.play('/assets/audio/grab.wav');

    const el = this.getElement(event.id);
    el.rotation = (2 + 5 * Math.random()) * (Math.random() < 0.5 ? -1 : 1);

    // update z index
    this.lastZIndex.set(this.lastZIndex() + 1);
    this.setOverride(el.uid, { zIndex: this.lastZIndex() });
  }

  async drag(event: IDragEvent) {
    const el = this.getElement(event.id);
    if (!el.draggable) {
      throw new Error(`Element '${el.uid}' is not draggable`);
    }

    const targets = this.elements().filter((el) => el.isDragTarget);
    for (const target of targets) {
      if (isInside(event.position, target.position, target.size)) {
        this.snap(el, target);
        return;
      }
    }

    if (el.draggable.isSnapped) {
      el.rotation = (2 + 5 * Math.random()) * (Math.random() < 0.5 ? -1 : 1);
      el.draggable.isSnapped = false;
    }

    this.setOverride(el.uid, {
      position: subtract(subtract(event.position, multiply(el.pivot, el.size)), el.position) as Vector2CanvasNormalized,
    });
  }

  async drop(event: IDragEvent) {
    this.#audioService.play('/assets/audio/drop.wav');
    this.#cursorService.clearGlobalCursor();

    const el = this.getElement(event.id);
    el.rotation = 0;

    const droppedInTargets = this.elements()
      .filter((el) => el.isDragTarget)
      .filter((target) => isInside(event.position, target.position, target.size));

    // add element to target
    if (droppedInTargets.length > 0) {
      for (const target of droppedInTargets) {
        const oldDraggableUid = this.targets()[target.uid];
        if (oldDraggableUid && oldDraggableUid !== el.uid) {
          const oldDraggableEl = this.getElement(oldDraggableUid);
          if (oldDraggableEl) {
            oldDraggableEl.draggable!.isSnapped = false;
            this.setOverride(oldDraggableEl.uid, { position: undefined });
          }
        }
        this.targets.set({ ...this.targets(), [target.uid]: el.uid });
      }
      return;
    }

    // remove element from old target
    const oldTarget = Object.entries(this.targets()).find(([, value]) => value === el.uid);
    if (oldTarget) {
      const [targetUid] = oldTarget;
      this.targets.set({ ...this.targets(), [targetUid]: undefined });
    }

    const isOutOfBounds =
      event.position[0] < 0 || event.position[0] > this.size()[0] || event.position[1] < 0 || event.position[1] > this.size()[1];
    if (isOutOfBounds) {
      this.setOverride(el.uid, { position: undefined });
      this.#audioService.play('/assets/audio/snap.wav');
      return;
    }

    const behaviour = this.activity().dropBehaviour;
    switch (behaviour) {
      case 'Free': {
        break;
      }
      case 'SnapBackToOrigin': {
        this.setOverride(el.uid, { position: undefined });
        this.#audioService.play('/assets/audio/snap.wav');
        break;
      }
      default: {
        // behaviour satisfies never; // TODO: uncomment this line when all behaviours are implemented
        throw new Error(`Drop behaviour '${behaviour}' not implemented`);
      }
    }
  }

  async continue() {
    await this.#lpMissionService.finishActivity(this.activity(), {
      DRAG_AND_DROP_ACTIVITY: {
        targets: this.targets(),
      },
    });
  }

  private getElement(id: Id) {
    const el = this.elements().find((el) => el.uid === id);
    if (!el) {
      throw new Error(`Element '${id}' not found`);
    }

    return el;
  }

  private setOverride(id: Id, override: Partial<{ position: Vector2CanvasNormalized; zIndex: number }>) {
    const oldOverrides = this.overrides();
    this.overrides.set({ ...oldOverrides, [id]: { ...(oldOverrides[id] ?? {}), ...override } });
  }

  private snap(el: ICanvasElement, target: ICanvasElement) {
    if (!el.draggable) {
      throw new Error(`Element '${el.uid}' is not draggable`);
    }

    if (!el.draggable.isSnapped) {
      this.#audioService.play('/assets/audio/snap.wav');
    }

    this.setOverride(el.uid, {
      position: subtract(
        subtract(add(target.position, multiply(target.pivot, target.size)), multiply(el.pivot, el.size)),
        el.position,
      ) as Vector2CanvasNormalized,
    });

    el.draggable.isSnapped = true;
    el.rotation = 0;
  }
}
